import { Dropdown, DropdownRef, Menu, Typography } from '@recruit-robin/robin-theme/web-components';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { useGetAddressQuery } from '@/shared/lib/address';
import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './AddressAutocomplete.styles';

type AddressAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  children: ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (target: React.ChangeEvent<HTMLInputElement>) => void;
  }) => React.ReactNode;
};

export const AddressAutocomplete = ({ value, onChange, children }: AddressAutocompleteProps) => {
  const classes = useStyles();
  const dictionary = useDictionary();
  const [search, setSearch] = useState<string>(value);
  const [searchDebounced, setSearchDebounced] = useState<string>(search);
  const dropdownRef = useRef<DropdownRef>(null);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  useDebounce(() => setSearchDebounced(search), 600, [search]);

  const { data: addresses = [], isFetching, status } = useGetAddressQuery({ search: searchDebounced });

  const isLoading = (isFetching || search !== searchDebounced) && (searchDebounced.length > 0 || search.length > 0);
  const hasNoResults = status === 'success' && addresses.length === 0;

  const handleSelect = (address: string) => {
    onChange(address);
    setSearch(address);

    dropdownRef.current?.close();
  };

  return (
    <Dropdown
      ref={dropdownRef}
      shouldUsePortal={false}
      flipMenu={false}
      placement="bottom-start"
      renderMenu={
        <Menu.Root customCss={classes.autocompleteMenu}>
          {search.length === 0 && addresses.length === 0 && (
            <Menu.Item.Root disabled customCss={classes.typeToSearchForAnAddress}>
              {dictionary.typeToSearchForAnAddress}
            </Menu.Item.Root>
          )}

          {isLoading ? (
            <>
              <Menu.Item.Skeleton />
              <Menu.Item.Skeleton />
              <Menu.Item.Skeleton />
            </>
          ) : (
            <>
              {status === 'success' &&
                addresses.map((address) => (
                  <Menu.Item.Root key={address.placeId} onClick={() => handleSelect(address.address)}>
                    {address.address}
                  </Menu.Item.Root>
                ))}

              {hasNoResults && (
                <Menu.Item.Root onClick={() => handleSelect(search)}>
                  <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
                    {search}
                  </Typography>
                </Menu.Item.Root>
              )}

              {status === 'error' && (
                <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
                  Error
                </Typography>
              )}
            </>
          )}
        </Menu.Root>
      }>
      {({ isOpen }) =>
        children({ value: isOpen ? search : value, onChange: (target) => setSearch(target.target.value) })
      }
    </Dropdown>
  );
};
